import {
  LightWeightObjectChangeTracker,
  SetOptions
} from '@schoolbelle/common/object-change-tracker';
import { QuestionBaseInterface } from './question.base.interface';
import { Survey } from '../../survey';
import { Option } from '../../option';
import { orderBy, values, pick, mapValues, omitBy } from 'lodash';
import { QuestionDependency } from './dependency';
import { QuestionDataInterface } from './questionData.interface';
import { setPriority } from '../../../functions';

export class QuestionBase extends LightWeightObjectChangeTracker
  implements QuestionBaseInterface {
  public _options: { [key: string]: Option } = {};
  get options(): Option[] {
    return orderBy(values(this._options), 'priority', 'asc');
  }

  get id(): string {
    return this.get(['id']);
  }
  get text(): string {
    return this.get(['text']);
  }
  set text(v: string) {
    this.set(['text'], v);
  }
  get type(): string {
    return this.get(['type']);
  }
  set type(v: string) {
    this.set(['type'], v);
  }
  get priority(): number {
    return this.get(['priority']);
  }
  set priority(v: number) {
    this.setQuestionPriority(v);
  }
  get max_checked_option_cnt(): number {
    return this.get(['max_checked_option_cnt']);
  }
  set max_checked_option_cnt(v: number) {
    this.set(['max_checked_option_cnt'], v);
  }
  get min_checked_option_cnt(): number {
    return this.get(['min_checked_option_cnt']);
  }
  set min_checked_option_cnt(v: number) {
    this.set(['min_checked_option_cnt'], v);
  }
  get required(): boolean {
    return this.min_checked_option_cnt > 0;
  }
  set required(v: boolean) {
    if (v) this.min_checked_option_cnt = 1;
    else this.min_checked_option_cnt = 0;
  }
  get canHaveQuotaOptions(): boolean {
    return this.get(['canHaveQuotaOptions']);
  }
  set canHaveQuotaOptions(v: boolean) {
    this.set(['canHaveQuotaOptions'], v);
    if (v === false)
      this.options
        .filter(o => o.quota != 0)
        .forEach(o => (o.quota = undefined));
  }
  dependency = new QuestionDependency(this);
  get value(): QuestionDataInterface {
    return {
      ...pick(this.get([]), [
        'id',
        'text',
        'type',
        'priority',
        'dependency',
        'max_checked_option_cnt',
        'min_checked_option_cnt',
        'canHaveQuotaOptions'
      ]),
      options: mapValues(
        omitBy(
          this._options,
          option => option.type === 'number' && !option.text
        ),
        option => option.value
      )
    };
  }

  constructor(public survey: Survey, question_id: string) {
    super(survey, ['questions', question_id]);
    this.set(['id'], question_id, { push: 'none' });
  }
  /**
   *
   * @param data
   * @param options
   * if no priority is set, set 1000, which will be modified to a proper value to put the question at the end of question list.
   * id, type, and priority are preset and should not be overriden.
   */
  public load(
    data: Partial<QuestionDataInterface> = {},
    options: SetOptions = undefined
  ) {
    //overide
    if (typeof data.priority === 'undefined')
      this.setQuestionPriority(1000, { push: 'none' });
    else this.setQuestionPriority(data.priority, { push: 'none' });
    this.set(
      [],
      {
        ...data,
        id: this.id,
        priority: this.priority
      },
      { push: 'none' }
    );
    this.notifyChangeThruTree([], options);
  }
  private setQuestionPriority(v_n: number, options: SetOptions = undefined) {
    setPriority(this, this.survey.questions, v_n, options);
  }
}
